import { Camera, Eye, EyeOff, Printer, ScanLine, Trash2, X } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { createPortal, flushSync } from 'react-dom'

import { Button, SelectField, StatusMessage, useSwipeDismiss } from '@krp/ui-kit'

import '../../../shared/ui/onec.css'
import './price-tags.css'
import { InputField } from '../../../shared/ui/InputField'
import { WarehouseSelect } from '../../structure/ui/WarehouseSelect'
import { metaOf } from '../../../shared/ui/meta'
import { BarcodeScanner } from '../../products/ui/scanner/BarcodeScanner'
import type { LabelTemplate } from '../model/templates'
import type { PriceTagItemVm, PriceTagsActions, PriceTagsVm } from '../model/types'
import { PriceTag } from './PriceTag'

/** Страховка для браузера без `afterprint`: лист вернётся в предпросмотр через минуту. */
const PRINT_FALLBACK_MS = 60_000

/**
 * Подбор и печать ценников одной базы 1С. Порядок как у полки: склад (от него
 * цена), шаблон, товары сканером, предпросмотр, печать.
 *
 * Предпросмотр — не украшение: печать без него сжигает рулон. Лист ценников —
 * один и тот же DOM в предпросмотре и в печати: на время печати он переносится
 * в корень документа (а не рисуется заново), остальное прячет `@media print`.
 * Печать — окном браузера на принтер по умолчанию: печати на сервере нет.
 */
export function PriceTagsScreen({ vm, actions }: { vm: PriceTagsVm; actions: PriceTagsActions }) {
  const [barcode, setBarcode] = useState('')
  const [scanning, setScanning] = useState(false)
  const [preview, setPreview] = useState(false)
  const [printing, setPrinting] = useState(false)
  const [capped, setCapped] = useState<string | null>(null)
  // Завершение печати: на мобильных `window.print()` не блокирует, конец печати — событие `afterprint`.
  const finishPrint = useRef<(() => void) | null>(null)
  useEffect(() => () => finishPrint.current?.(), [])

  const chosen = vm.warehouses.selected !== ''
  // Поле не гаснет на время запроса: иначе сканер-клавиатура теряет фокус.
  const scanDisabled = !chosen
  const submitScan = (code: string) => {
    if (code.trim() === '' || scanDisabled || vm.scan.busy) return
    actions.scanSubmitted(code)
    setBarcode('')
  }

  const print = () => {
    // Лист обязан отрисоваться ДО окна печати: со скрытым предпросмотром печаталась бы пустая страница.
    flushSync(() => {
      setPreview(true)
      setPrinting(true)
    })
    // Размер страницы — по шаблону: одна этикетка на страницу, без полей. Правило живёт только на
    // время печати: в предпросмотре оно ни к чему.
    finishPrint.current?.()
    const page = document.createElement('style')
    page.textContent = `@page { size: ${vm.template.widthMm}mm ${vm.template.heightMm}mm; margin: 0; }`
    document.head.appendChild(page)
    // Окно печати закрыто: правило снято, лист возвращается в предпросмотр. Ровно один раз.
    const finish = () => {
      if (finishPrint.current !== finish) return
      finishPrint.current = null
      window.removeEventListener('afterprint', finish)
      clearTimeout(timer)
      page.remove()
      setPrinting(false)
    }
    finishPrint.current = finish
    window.addEventListener('afterprint', finish)
    const timer = setTimeout(finish, PRINT_FALLBACK_MS)
    try {
      window.print()
    } catch (error) {
      finish()
      throw error
    }
  }

  const sheet = (
    <div className="onec-tags__sheet" aria-label="Предпросмотр ценников" data-printing={printing || undefined}>
      {vm.items.flatMap((item) => copiesOf(item, vm.template))}
    </div>
  )

  return (
    <section className="onec-screen onec-tags" aria-labelledby="onec-tags-title">
      <header className="onec-toolbar">
        <h2 id="onec-tags-title">Печать ценников</h2>
      </header>

      <WarehouseSelect
        label="Склад этой базы — от него цена"
        value={vm.warehouses.selected}
        pick={vm.warehouses.pick}
        placeholder="— выберите склад —"
        disabled={vm.scan.busy}
        onChange={actions.warehouseChosen}
        onRetry={actions.reloadWarehouses}
      />
      {chosen && vm.items.length > 0 && (
        <p className="onec-field__note">Смена склада очистит подбор: цены в нём — цены этого склада.</p>
      )}

      <SelectField label="Шаблон ценника" value={vm.template.id} options={vm.templates} onChange={actions.templateChosen} />

      <div className="onec-search" role="search">
        <InputField
          type="search"
          label="Штрихкод товара"
          value={barcode}
          onChange={setBarcode}
          onEnter={() => submitScan(barcode)}
          disabled={scanDisabled}
          autoFocus
        />
        <div className="onec-search__buttons">
          <Button
            variant="primary"
            icon={<ScanLine size={16} aria-hidden="true" />}
            busy={vm.scan.busy}
            disabled={scanDisabled || vm.scan.busy || barcode.trim() === ''}
            onClick={() => submitScan(barcode)}
          >
            Добавить
          </Button>
          {vm.cameraScanAvailable && (
            <Button icon={<Camera size={16} aria-hidden="true" />} disabled={scanDisabled} onClick={() => setScanning(true)}>
              Камера
            </Button>
          )}
        </div>
      </div>
      {!chosen && <p>Сначала выберите склад — цена на ценнике берётся с него.</p>}
      {vm.scan.notFound !== null && <StatusMessage kind="empty" title="Не найден" text={vm.scan.notFound} />}
      {vm.scan.error !== null && (
        <StatusMessage
          kind="error"
          title="Скан не выполнен"
          text={vm.scan.error.message}
          meta={metaOf(vm.scan.error.code, vm.scan.error.correlationId)}
        />
      )}

      {vm.items.length === 0 ? (
        <StatusMessage
          kind="empty"
          title="Ценников пока нет"
          text="Отсканируйте товары — повторный скан добавляет ещё один ценник того же товара."
        />
      ) : (
        <>
          <ul className="onec-tags__items" aria-label="Подбор ценников">
            {vm.items.map((item) => (
              <PriceTagRow
                key={item.productRefKey}
                item={item}
                maxCopies={vm.maxCopies}
                capped={capped === item.productRefKey}
                onCopies={(copies) => {
                  setCapped(copies > vm.maxCopies ? item.productRefKey : null)
                  actions.copiesChanged(item.productRefKey, copies)
                }}
                onRemove={() => actions.itemRemoved(item.productRefKey)}
              />
            ))}
          </ul>

          <footer className="onec-tags__footer">
            <p className="onec-tags__totals" aria-live="polite">
              Позиций: <b>{vm.totals.positions}</b> · ценников к печати: <b>{vm.totals.copies}</b>
              {/* Подбор переживает перезагрузку, а цены в нём — снимок момента скана. */}
              {vm.pricesAt !== null && (
                <>
                  {' '}
                  · цены на <b>{vm.pricesAt}</b>
                  {vm.pricesOld ? ' (старше суток — отсканируйте заново перед печатью)' : ''}
                </>
              )}
              {vm.pricesUnknown > 0 && (
                <>
                  {' '}
                  · цены из обхода: <b>{vm.pricesUnknown}</b> (время скана неизвестно — отсканируйте заново перед печатью)
                </>
              )}
              {vm.totals.withoutPrice > 0 && (
                <>
                  {' '}
                  · без цены: <b>{vm.totals.withoutPrice}</b> (не напечатаются)
                </>
              )}
            </p>
            <div className="onec-actions">
              <Button variant="ghost" icon={<Trash2 size={16} aria-hidden="true" />} onClick={actions.cleared}>
                Очистить подбор
              </Button>
              <Button
                icon={preview ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
                aria-pressed={preview}
                onClick={() => setPreview((shown) => !shown)}
              >
                {preview ? 'Скрыть предпросмотр' : 'Предпросмотр'}
              </Button>
              <Button
                variant="primary"
                icon={<Printer size={16} aria-hidden="true" />}
                disabled={vm.totals.copies === 0}
                onClick={print}
              >
                Печать ценников
              </Button>
            </div>
          </footer>

          <p className="onec-tags__hint">
            Печать идёт окном печати браузера на принтер по умолчанию. Размер страницы задаёт шаблон (
            {vm.template.widthMm}×{vm.template.heightMm} мм, одна этикетка — одна страница); в окне печати — поля «Нет»,
            масштаб 100%.
          </p>
        </>
      )}

      {preview && vm.items.length > 0 && (printing ? createPortal(sheet, document.body) : sheet)}

      {scanning && (
        <BarcodeScanner
          onClose={() => setScanning(false)}
          onCode={(code) => {
            setScanning(false)
            submitScan(code)
          }}
        />
      )}
    </section>
  )
}

/** Позиция подбора. Убирается жестом влево по шапке или кнопкой — без подтверждения (решение 264). */
function PriceTagRow({
  item,
  maxCopies,
  capped,
  onCopies,
  onRemove,
}: {
  item: PriceTagItemVm
  maxCopies: number
  capped: boolean
  onCopies: (copies: number) => void
  onRemove: () => void
}) {
  const swipe = useSwipeDismiss({ axis: 'left', onDismiss: onRemove })
  const copiesId = useId()
  // Текст поля — свой: поле можно стереть, чтобы набрать «5», а не получить «15».
  const [text, setText] = useState(String(item.copies))
  const [shown, setShown] = useState(item.copies)
  if (shown !== item.copies) {
    setShown(item.copies)
    if (Number(text) !== item.copies) setText(String(item.copies))
  }
  return (
    <li className="onec-tags__item" data-priced={item.price !== null} style={swipe.style}>
      <div className="onec-tags__grip" {...swipe.bind()}>
        <span className="onec-tags__name">{item.name}</span>
        <span className="onec-tags__barcode">{item.barcode}</span>
      </div>
      <span className="onec-tags__price">
        {/* Товар без цены назван, а не пропущен молча: пустой ценник повесят на полку и не заметят. */}
        {item.price === null ? <X size={14} aria-hidden="true" /> : null} {item.priceText}
      </span>
      <div className="krp-field onec-tags__copies">
        <label className="krp-field__label" htmlFor={copiesId}>
          Ценников
        </label>
        <input
          id={copiesId}
          type="number"
          min={1}
          max={maxCopies}
          value={text}
          onChange={(event) => {
            setText(event.target.value)
            if (event.target.value.trim() !== '') onCopies(Number(event.target.value))
          }}
          onBlur={() => setText(String(item.copies))}
        />
        {capped && (
          <span className="onec-field__note" role="status">
            не больше {maxCopies}
          </span>
        )}
      </div>
      <Button
        variant="ghost"
        icon={<Trash2 size={16} aria-hidden="true" />}
        aria-label={`Убрать «${item.name}»`}
        onClick={onRemove}
      >
        Убрать
      </Button>
    </li>
  )
}

/** Ценники одной позиции: по одному на экземпляр; без цены — ни одного. */
function copiesOf(item: PriceTagItemVm, template: LabelTemplate) {
  const price = item.price
  if (price === null) return []
  return Array.from({ length: item.copies }, (_unused, index) => (
    <PriceTag key={`${item.productRefKey}-${index}`} name={item.name} price={price} barcode={item.barcode} template={template} />
  ))
}
